import {
  IBatch,
  ICreate,
  IGet,
  IRemoteUpdate,
  IReportList,
  IUpdate,
  batch,
  remoteUpdate,
  seed,
  update,
} from '../../actions/admin/data';
import {
  cloneRequest,
  copyRequest,
  createRequest,
  getRequest,
  importModuleRequest,
  listRequest,
  moveRequest,
  updateRequest,
  reportRequest,
  getResponsesRequest,
  resetRequest,
  batchRequest,
  generateActionAiImageRequest,
  generateActionAiModelRequest,
} from '../../services/admin';
import {
  actionRequest,
  attemptRequest,
  completeRequest,
  dialogueRequest,
  groupPermissionRequest,
  homeRequest,
  learnerForumPostCreateRequest,
  learnerForumTopicCreateRequest,
  learnerForumTopicRequest,
  learnerForumTopicsRequest,
  moduleRequest,
  searchRequest,
  systemPermissionRequest,
  tokenRequest,
  verificationRequest,
} from '../../services/product';
import { LoadingState, SavingState } from 'types/domain/base';
import merge, { mergeCreate } from './utils/merge';
import { applyTextSave } from './utils/translation';
import {
  IActionAction,
  IAttemptAction,
  IGroupPermissionResponse,
  IModuleAction,
  IPlayerResponse,
  ITokenAction,
  ITokenActionResponse,
  IVerificationAction,
} from 'types/action/player';
import { EntityTypes } from 'constants/entities';
import { copy, mergeCopyOffline } from './utils/copy';
import { IMove, IReset } from 'core/actions/admin/diagram';
import { ISessionAttempt } from 'types/domain/session';
import { IModuleDAO } from 'types/domain/module';
import { ICourseNodeDAO } from 'types/domain/course';
import { ISceneDAO } from 'types/domain/scene';
import { hydrate } from 'core/actions/product/state';
import { ISearch, ISearchResponse, SearchType } from 'types/domain/search';
import { uploadActions } from 'core/slices/upload';
import { IEntityReducer } from '.';
import { systemSigninRequest } from 'core/services/authentication';
import { ForumTopicStatus } from 'types/domain/forum';
import ThemePage from 'app/pages/settings/theme/ThemePage';

export function cases(builder) {
  builder
    .addCase(hydrate, (state, action) => {
      if (action.payload.prodEnt)
        Object.keys(action.payload.prodEnt).map(type => {
          if (state[type]) state[type] = { ...state[type], ...action.payload.prodEnt[type] };
        });
    })
    .addCase(batch, (state, action) => {
      const { batch: batchItems } = action.payload as IBatch;
      batchItems.forEach(item => {
        merge(state, item.type, item.entity.id, item.entity);

        const { parentEntityAttribute, parentEntityType, parentEntityId } = item;

        if (parentEntityAttribute && state[parentEntityType])
          merge(state, parentEntityType, parentEntityId, {
            id: parentEntityId,
            [parentEntityAttribute]: item.entity.id,
          });
      });
    })
    .addCase(seed, (state, action) => {
      merge(state, action.payload.type, null, action.payload.list, true);
    })
    .addCase(remoteUpdate, (state, action) => {
      const { type, entity, parentModuleId, mergeEntity } = action.payload as IRemoteUpdate;
      if (!state[type] || !entity?.id) return;

      if (mergeEntity) merge(state, type, entity.id, entity);
      applyTextSave(state, { type, entity, parentModuleId }, entity);
    })
    .addCase(update, (state, action) => {
      const { entity, type, parentEntityAttribute, parentEntityType, parentEntityId, mergeData } =
        action.payload as IUpdate;

      //return a new state to syncronise undos
      if (!state[type]) return { ...state };

      if (mergeData) merge(state, type, entity.id, entity);
      else {
        const entities = get(state, type);
        entities[entity.id] = {
          ...(entities[entity.id] || {}),
          ...entity,
        };
        state[type] = entities;
      }

      if (parentEntityAttribute && state[parentEntityType])
        merge(state, parentEntityType, parentEntityId, { id: parentEntityId, [parentEntityAttribute]: entity.id });
    })
    .addCase(updateRequest.pending, (state, action) => {
      const { type, entity } = action.meta.arg as IUpdate;
      //updateSaving(state, type, entity.id, SavingState.SAVING, null);
    })

    .addCase(updateRequest.rejected, (state, action) => {
      const { type, entity } = action.meta.arg as IUpdate;
      updateSaving(state, type, entity?.id, SavingState.ERROR, action.error.message);
    })
    .addCase(updateRequest.fulfilled, (state, action) => {
      const { type, entity, mergeData } = action.meta.arg as IUpdate;
      //updateSaving(state, type, entity.id, SavingState.SAVED, null);
      if (mergeData) merge(state, type, entity.id, action.payload);
      applyTextSave(state, action.meta.arg as IUpdate, action.payload);
    })
    .addCase(batchRequest.pending, (state, action) => {
      const { batch } = action.meta.arg as IBatch;
      batch.forEach(update => {
        merge(state, update.type, update.entity.id, update.entity);

        const { parentEntityAttribute, parentEntityType, parentEntityId } = update;

        if (parentEntityAttribute && state[parentEntityType])
          merge(state, parentEntityType, parentEntityId, {
            id: parentEntityId,
            [parentEntityAttribute]: update.entity.id,
          });
      });
    })
    .addCase(batchRequest.fulfilled, (state, action) => {
      const { batch } = action.meta.arg as IBatch;

      batch.forEach((update, i) => {
        const entity = action.payload[i];

        if (!entity?.id) return;

        merge(state, update.type, entity.id, entity);

        const { parentEntityAttribute, parentEntityType, parentEntityId } = update;

        if (parentEntityAttribute)
          merge(state, parentEntityType, parentEntityId, {
            id: parentEntityId,
            [parentEntityAttribute]: entity.id,
          });
      });
    })
    .addCase(getRequest.pending, (state, action) => {
      const { type, entityId } = action.meta.arg as IGet;
      updateLoading(state, type, entityId, LoadingState.LOADING, null);
    })
    .addCase(getRequest.rejected, (state, action) => {
      const { type, entityId } = action.meta.arg as IGet;
      updateLoading(state, type, entityId, LoadingState.ERROR, action.error.message);
    })
    .addCase(getRequest.fulfilled, (state, action) => {
      const { type, entityId } = action.meta.arg as IGet;
      updateLoading(state, type, entityId, LoadingState.LOADED, null);
      merge(state, type, entityId, action.payload);
    })
    .addCase(reportRequest.pending, (state, action) => {
      const { type, entityId } = action.meta.arg as IGet;
      updateLoading(state, type, entityId, LoadingState.LOADING, null);
    })
    .addCase(reportRequest.rejected, (state, action) => {
      const { type, entityId } = action.meta.arg as IGet;
      updateLoading(state, type, entityId, LoadingState.ERROR, action.error.message);
    })
    .addCase(reportRequest.fulfilled, (state, action) => {
      const { type, reportType } = action.meta.arg as IReportList;
      updateLoading(state, type, null, LoadingState.LOADED, null);
      merge(state, type, null, action.payload.list);
    })
    .addCase(getResponsesRequest.pending, (state, action) => {
      const { type, entityId } = action.meta.arg as IGet;
      updateLoading(state, type, entityId, LoadingState.LOADING, null);
    })
    .addCase(getResponsesRequest.rejected, (state, action) => {
      const { type, entityId } = action.meta.arg as IGet;
      updateLoading(state, type, entityId, LoadingState.ERROR, action.error.message);
    })
    .addCase(getResponsesRequest.fulfilled, (state, action) => {
      const { type, entityId } = action.meta.arg as IGet;
      updateLoading(state, type, entityId, LoadingState.LOADED, null);
      merge(state, type, entityId, action.payload);
    })
    .addCase(listRequest.pending, (state, action) => {
      const { type } = action.meta.arg as IGet;
      updateLoading(state, type, null, LoadingState.LOADING, null);
    })
    .addCase(listRequest.rejected, (state, action) => {
      const { type } = action.meta.arg as IGet;
      updateLoading(state, type, null, LoadingState.ERROR, action.error.message);
    })
    .addCase(listRequest.fulfilled, (state, action) => {
      const { type } = action.meta.arg as IGet;

      updateLoading(state, type, null, LoadingState.LOADED, null);

      merge(state, type, null, action.payload.list);
    })
    .addCase(createRequest.pending, (state, action) => {
      const { type, entity, parentEntityType, parentEntityAttribute, parentEntityId } = action.meta.arg as ICreate;
      merge(state, type, entity.offlineId, { id: entity.offlineId, ...entity });

      if (parentEntityAttribute)
        merge(state, parentEntityType, parentEntityId, { id: parentEntityId, [parentEntityAttribute]: entity.id });

      //  if(type === EntityTypes.ACTION)
    })
    .addCase(createRequest.rejected, (state, action) => {
      const { type, offlineId } = action.meta.arg;
      updateSaving(state, type, offlineId, SavingState.ERROR, action.error.message);
    })
    .addCase(createRequest.fulfilled, (state, action) => {
      mergeCreate(state, action);
      applyTextSave(state, action.meta.arg as ICreate, action.payload);
    })
    .addCase(cloneRequest.pending, (state, action) => {
      const { type, id } = action.meta.arg;
      updateLoading(state, type, id, LoadingState.LOADING, null);
    })
    .addCase(cloneRequest.rejected, (state, action) => {
      const { type, id, error } = action.meta.arg;
      updateLoading(state, type, id, LoadingState.ERROR, error);
    })
    .addCase(cloneRequest.fulfilled, mergeCreate)
    .addCase(copyRequest.pending, copy)
    .addCase(copyRequest.rejected, (state, action) => {
      const { type, id, error } = action.meta.arg;
      updateLoading(state, type, id, LoadingState.ERROR, error);
    })
    .addCase(copyRequest.fulfilled, mergeCopyOffline)
    .addCase(moveRequest.pending, (state, action) => {
      const { items, offset, parentEntityId, parentEntityType } = action.meta.arg as IMove;

      if (parentEntityType === EntityTypes.MODULE)
        items.forEach(item => {
          if (state[item.type][item.id]) state[item.type][item.id].moduleId = parentEntityId;
        });
      else
        items.forEach(item => {
          if (state[item.type][item.id] && item.order) state[item.type][item.id].order = item.order;
        });

      // merge(state, EntityTypes.NODE, null, items);
    })
    .addCase(moveRequest.rejected, (state, action) => {
      const { type, id, error } = action.meta.arg;
      updateLoading(state, type, id, LoadingState.ERROR, error);
    })
    .addCase(moveRequest.fulfilled, (state, action) => {
      const { items, parentEntityId, parentEntityType } = action.meta.arg as IMove;
      const { nodes } = action.payload;

      if (nodes) merge(state, EntityTypes.NODE, null, nodes);
    })
    .addCase(resetRequest.pending, (state: IEntityReducer, action) => {
      const { ids, parentEntityId, parentEntityType } = action.meta.arg as IReset;

      if (parentEntityType === EntityTypes.MODULE) {
        Object.values(state[EntityTypes.ACTION])
          .filter(a => a.moduleId === parentEntityId)
          .forEach(a => {
            state[EntityTypes.ACTION][a.id].nodeId = null;
          });
      } else if (parentEntityType === EntityTypes.COURSE) {
        Object.values(state[EntityTypes.COURSE_NODE])
          .filter(a => a.courseId === parentEntityId)
          .forEach(a => {
            state[EntityTypes.COURSE_NODE][a.id].nodeId = null;
          });
      }
    })
    .addCase(resetRequest.rejected, (state, action) => {
      const { type, id, error } = action.meta.arg;
      updateLoading(state, type, id, LoadingState.ERROR, error);
    })
    .addCase(tokenRequest.fulfilled, (state, action) => {
      if (!action.payload) return;

      const { orgConfig, theme } = action.payload as ITokenActionResponse;
      const { token, tokenCreated } = action.meta.arg as ITokenAction;
      if (orgConfig) merge(state, EntityTypes.ORG_CONFIG, orgConfig.id, orgConfig);
      if (theme) merge(state, EntityTypes.THEME, theme.id, theme);
      mergePlayerResponses(state, action.payload.playerResponse);
    })
    .addCase(systemSigninRequest.fulfilled, (state, action) => {
      if (!action.payload) return;

      const { orgConfig, theme } = action.payload as ITokenActionResponse;
      const { token, tokenCreated } = action.meta.arg as ITokenAction;
      if (orgConfig) merge(state, EntityTypes.ORG_CONFIG, orgConfig.id, orgConfig);
      if (theme) merge(state, EntityTypes.THEME, theme.id, theme);
      mergePlayerResponses(state, action.payload.playerResponse);
    })
    .addCase(groupPermissionRequest.fulfilled, (state, action) => {
      const { permission } = action.payload as IGroupPermissionResponse;

      if (permission) merge(state, EntityTypes.COURSE_PROGRESS, permission.id, permission);
    })
    .addCase(verificationRequest.pending, (state, action) => {
      const { verificationRequestId } = action.meta.arg as IVerificationAction;
      updateSaving(state, EntityTypes.VERIFICATION_REQUEST, verificationRequestId, SavingState.SAVING, null);
    })
    .addCase(verificationRequest.rejected, (state, action) => {
      const { verificationRequestId } = action.meta.arg as IVerificationAction;
      updateSaving(
        state,
        EntityTypes.VERIFICATION_REQUEST,
        verificationRequestId,
        SavingState.ERROR,
        action.error.message,
      );
    })
    .addCase(verificationRequest.fulfilled, (state, action) => {
      const { verificationRequestId } = action.meta.arg as IVerificationAction;
      updateSaving(state, EntityTypes.VERIFICATION_REQUEST, verificationRequestId, SavingState.SAVED, null);
      mergePlayerResponses(state, action.payload);
    })
    .addCase(systemPermissionRequest.fulfilled, (state, action) => {
      mergePlayerResponses(state, action.payload);
    })
    .addCase(actionRequest.fulfilled, (state, action) => {
      const { token, tokenCreated, actionId } = action.meta.arg as IActionAction;
      mergePlayerResponses(state, action.payload);
    })
    .addCase(homeRequest.fulfilled, (state, action) => {
      mergePlayerResponses(state, action.payload);
    })
    .addCase(completeRequest.fulfilled, (state, action) => {
      const { token, tokenCreated, actionId } = action.meta.arg as IActionAction;
      mergePlayerResponses(state, action.payload);
    })
    .addCase(importModuleRequest.pending, (state, action) => {
      const { offlineId } = action.meta.arg;
      merge(state, EntityTypes.MODULE, offlineId, { id: offlineId, offlineId });
    })
    .addCase(importModuleRequest.rejected, (state, action) => {
      const { offlineId } = action.meta.arg;
      updateLoading(state, EntityTypes.MODULE, offlineId, LoadingState.ERROR, action.error.message);
    })
    .addCase(importModuleRequest.fulfilled, (state, action) => {
      const { offlineId, courseId, parentEntityId, parentEntityAttribute } = action.meta.arg;
      if (courseId !== undefined) {
        const courseNode = action.payload as ICourseNodeDAO;
        merge(state, EntityTypes.COURSE_NODE, courseNode.id, {
          ...courseNode,
          offlineId,
          loading: LoadingState.LOADED,
          error: null,
        });
        if (parentEntityAttribute && parentEntityId)
          merge(state, EntityTypes.COURSE_NODE, parentEntityId, {
            id: parentEntityId,
            [parentEntityAttribute]: courseNode.id,
          });
      } else {
        const module = action.payload as IModuleDAO;
        merge(state, EntityTypes.MODULE, module.id, {
          ...module,
          offlineId,
          loading: LoadingState.LOADED,
          error: null,
        });
      }
    })
    .addCase(generateActionAiImageRequest.fulfilled, (state, action) => {
      const scene = action.payload as ISceneDAO;

      // The endpoint no longer touches the action: it returns the generated scene, with
      // scene.actionId recording what it was generated for. Merging it makes the scene
      // available to the panel that lists a slide's generated images.
      merge(state, EntityTypes.SCENE, scene.id, { ...scene, loading: LoadingState.LOADED, error: null });
    })
    .addCase(generateActionAiModelRequest.fulfilled, (state, action) => {
      const scene = action.payload as ISceneDAO;

      // The placeholder for a model still being built, stamped with the action it is for and
      // with no model yet. The websocket updates it in place when the build finishes or fails.
      merge(state, EntityTypes.SCENE, scene.id, { ...scene, loading: LoadingState.LOADED, error: null });
    })
    .addCase(moduleRequest.pending, (state, action) => {
      const { id } = action.meta.arg as IModuleAction;
      updateLoading(state, EntityTypes.MODULE, id, LoadingState.LOADING, null);
    })
    .addCase(moduleRequest.rejected, (state, action) => {
      const { id } = action.meta.arg as IModuleAction;
      updateLoading(state, EntityTypes.MODULE, id, LoadingState.ERROR, action.error);
    })
    .addCase(moduleRequest.fulfilled, (state, action) => {
      const module = action.payload as IModuleDAO;

      merge(state, EntityTypes.MODULE, module.id, { ...module, loading: LoadingState.LOADED, error: null });
    })
    .addCase(learnerForumTopicsRequest.fulfilled, (state, action) => {
      merge(state, EntityTypes.FORUM_TOPIC, null, action.payload);
    })
    .addCase(learnerForumTopicRequest.fulfilled, (state, action) => {
      merge(state, EntityTypes.FORUM_TOPIC, action.payload?.id, action.payload);
    })
    // Learner creates show straight away under a local id and take the server's id
    // when it lands (merge re-keys on offlineId). Neither response names the author,
    // so the signed-in learner stands in.
    .addCase(learnerForumTopicCreateRequest.pending, (state, action) => {
      const { offlineId, courseId, moduleId, name, author } = action.meta.arg;
      merge(state, EntityTypes.FORUM_TOPIC, offlineId, {
        id: offlineId,
        offlineId,
        courseId,
        moduleId,
        name,
        status: ForumTopicStatus.OPEN,
        organisationUser: author,
      });
    })
    .addCase(learnerForumTopicCreateRequest.rejected, (state, action) => {
      delete state[EntityTypes.FORUM_TOPIC][action.meta.arg.offlineId];
    })
    .addCase(learnerForumTopicCreateRequest.fulfilled, (state, action) => {
      const { offlineId, author } = action.meta.arg;
      merge(state, EntityTypes.FORUM_TOPIC, action.payload.id, {
        ...action.payload,
        offlineId,
        organisationUser: action.payload.organisationUser ?? author,
      });
    })
    .addCase(learnerForumPostCreateRequest.pending, (state, action) => {
      const { offlineId, forumTopicId, parentPostId, moduleId, type, text, author } = action.meta.arg;
      merge(state, EntityTypes.FORUM_POST, offlineId, {
        id: offlineId,
        offlineId,
        forumTopicId,
        parentPostId,
        moduleId,
        type,
        organisationUser: author,
        textId: offlineId,
        text: { ...text, id: offlineId },
      });
    })
    .addCase(learnerForumPostCreateRequest.rejected, (state, action) => {
      const { offlineId } = action.meta.arg;
      delete state[EntityTypes.FORUM_POST][offlineId];
      delete state[EntityTypes.TEXT][offlineId];
    })
    .addCase(learnerForumPostCreateRequest.fulfilled, (state, action) => {
      const { offlineId, text, type, author } = action.meta.arg;
      const post = action.payload;

      // The create echoes the text's new id rather than its content, so the text
      // that was sent moves across to it. Without an id it stays where it is.
      const movedText = post.text ?? (post.textId ? { ...text, id: post.textId } : undefined);
      if (movedText) delete state[EntityTypes.TEXT][offlineId];

      merge(state, EntityTypes.FORUM_POST, post.id, {
        ...post,
        offlineId,
        type: post.type ?? type,
        organisationUser: post.organisationUser ?? author,
        text: movedText,
      });
    })
    .addCase(attemptRequest.pending, (state, action) => {
      const { offlineId, courseProgressId } = action.meta.arg as IAttemptAction;
      updateSaving(state, EntityTypes.SESSION_ATTEMPT, courseProgressId, SavingState.SAVING, null);
      //merge(state, EntityTypes.ACCESS_PERMISSION, courseProgressId, { sessionAttemptId: offlineId });
    })
    .addCase(attemptRequest.rejected, (state, action) => {
      const { courseProgressId } = action.meta.arg as IAttemptAction;
      updateSaving(state, EntityTypes.SESSION_ATTEMPT, courseProgressId, SavingState.ERROR, action.error.message);
    })
    .addCase(attemptRequest.fulfilled, (state, action) => {
      const sessionAttempt = action.payload as ISessionAttempt;
      updateSaving(state, EntityTypes.SESSION_ATTEMPT, sessionAttempt.id, SavingState.SAVED, null);
      merge(state, EntityTypes.SESSION_ATTEMPT, sessionAttempt.id, sessionAttempt);
    })
    .addCase(dialogueRequest.fulfilled, (state, action) => {
      mergePlayerResponses(state, action.payload);
    })

    .addCase(uploadActions.uploadProcessing, (state, action) => {
      if (action.payload.type === EntityTypes.SCENE)
        merge(state, EntityTypes.SCENE, action.payload.entityId, {
          id: action.payload.entityId,
          progress: action.payload.entity,
        });
    })
    .addCase(searchRequest.fulfilled, (state, action) => {
      const { searchType } = action.meta.arg as ISearch;
      const searchResponse = action.payload as ISearchResponse;

      if (searchResponse.results?.list) {
        if ([SearchType.LEARNER, SearchType.MODULE].includes(searchType))
          merge(state, EntityTypes.TEXT, null, searchResponse.results.list);

        if (searchType === SearchType.ORG_USER) merge(state, EntityTypes.ORG_USER, null, searchResponse.results.list);
      }
    });

  return builder;
}

function mergePlayerResponses(state, payload) {
  if (!payload) return;

  const { queue } = payload as IPlayerResponse;

  queue.forEach(item => {
    if (item.module) merge(state, EntityTypes.MODULE, item.module.id, item.module);
    if (item.courseNode) merge(state, EntityTypes.COURSE_NODE, item.courseNode.id, item.courseNode);
    if (item.courseNodeMenu) merge(state, EntityTypes.COURSE_NODE, item.courseNodeMenu.id, item.courseNodeMenu);
  });
}

function updateLoading(state, type, id, loading, error) {
  const entities = get(state, type);

  if (!state[type]) return state;

  id
    ? (entities[id] = {
        ...(entities[id] || {}),
        loading,
        error,
      })
    : (entities.loading = loading);

  state[type] = entities;
}

function updateSaving(state, type, id, saving, error) {
  const entities = get(state, type);

  if (!state[type]) return state;

  id
    ? (entities[id] = {
        ...(entities[id] || {}),
        saving,
        error,
      })
    : (entities.saving = saving);

  state[type] = entities;
}

export function get(state, type) {
  return { ...state[type] };
}
